import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Skeleton,
    Stack,
    alpha,
    useTheme,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { Button, FoodsIllustrations, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { ApproximateImpactTooltip } from "src/components/Actions/Common/ApproximateImpactTooltip";
import { ExtensionPoint, EXTENSION_POINTS } from "src/components/PuckExtensions";
import { getScorePrecision } from "src/context/Scores/provider";
import { formatImpact, getImpactColor } from "src/context/Scores/utils/impactDisplay";
import { useCreationTakingLonger } from "./useCreationTakingLonger";
import {
    EstimateErrorAlert,
    InvitationNoticeAlert,
    type EstimateErrorReason,
    type InvitationNotice,
} from "./TemplateUseAlerts";

export type InvitationScore = {
    id: number;
    title: string;
    improvement: "increase" | "decrease";
    unit?: { abbreviation: string };
    precision?: number;
};
export type InvitationProjection =
    | { status: "loading" }
    | { status: "error"; reason?: EstimateErrorReason }
    | { status: "conflict" }
    | { status: "all-dropped" }
    | { status: "success"; values: Record<string, number | null> };

type Props = {
    firstName: string;
    ssqTitle: string;
    campaign: string;
    proposed: boolean;
    targetYear?: number;
    simplified: boolean;
    scores: InvitationScore[];
    projection: InvitationProjection;
    busy: boolean;
    creationType?: "adopt" | "create-own";
    failed: boolean;
    creationUnconfirmed?: boolean;
    retryAdoption?: boolean;
    adoptDisabled?: boolean;
    notice?: InvitationNotice;
    onRetryEstimate?: () => void;
    onClose: () => void;
    onCheckCreation: () => void;
    onCreateOwn: () => void;
    onAdopt: () => void;
};

export const ActionPlanInvitationDialog = (props: Props) => {
    const {
        firstName,
        ssqTitle,
        campaign,
        proposed,
        targetYear,
        simplified,
        scores,
        projection,
        busy,
        creationType,
        failed,
        creationUnconfirmed,
        retryAdoption,
        adoptDisabled,
        notice,
        onRetryEstimate,
        onClose,
        onCheckCreation,
        onCreateOwn,
        onAdopt,
    } = props;
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const illustrationSize = Number.parseFloat(theme.spacing(10));
    const takingLonger = useCreationTakingLonger(busy);

    const close = () => {
        if (!busy) onClose();
    };
    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            onClose={close}
            disableEscapeKeyDown={busy}
            aria-labelledby="action-plan-invitation-title"
            aria-describedby="action-plan-invitation-context"
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ p: theme.spacing(2), borderBottom: 1, borderColor: "divider" }}
            >
                <Typography variant="h5" id="action-plan-invitation-title">
                    {t("action_plan.invitation.title", "Are you ready to take action?")}
                </Typography>
                <IconButton onClick={close} disabled={busy} aria-label={t("action_plan.invitation.close", "Close")}>
                    <Close />
                </IconButton>
            </Stack>
            <DialogContent sx={{ p: theme.spacing(3) }}>
                <Stack gap={theme.spacing(3)}>
                    <FoodsIllustrations
                        illustration="congratulations"
                        width={illustrationSize}
                        height={illustrationSize}
                    />
                    <Typography component="p" variant="body-regular" id="action-plan-invitation-context">
                        {t(
                            "action_plan.invitation.context",
                            "Well done {{firstName}}! You have validated your {{ssqTitle}} for {{campaign}}.",
                            { firstName, ssqTitle, campaign },
                        )}
                    </Typography>
                    <ExtensionPoint extensionPoint={EXTENSION_POINTS.ACTION_PLAN_INVITATION_AFTER_CONTEXT} />
                    {proposed ?
                        <>
                            {projection.status !== "all-dropped" && projection.status !== "conflict" && (
                                <Typography component="p" variant="body-regular">
                                    {projection.status === "error" ?
                                        targetYear === undefined ?
                                            t(
                                                "action_plan.invitation.recommended_without_results",
                                                "Based on this, we have prepared an action plan with the actions recommended for you.",
                                            )
                                        :   t(
                                                "action_plan.invitation.recommended_without_estimate",
                                                "Based on this, we have prepared an action plan with the actions recommended for you, with {{targetYear}} as the target year.",
                                                { targetYear },
                                            )

                                    : simplified ?
                                        t(
                                            "action_plan.creation.results_intro",
                                            "Based on this, we have prepared an action plan with the actions recommended for you, aiming for the following results by {{targetYear}}:",
                                            { targetYear },
                                        )
                                    :   t(
                                            "action_plan.invitation.recommended",
                                            "Based on this, we have prepared an action plan with the actions recommended for you, aiming for the following results:",
                                        )
                                    }
                                </Typography>
                            )}
                            {projection.status === "all-dropped" ?
                                <Typography component="p" variant="body-regular" role="status">
                                    {t(
                                        "action_plan.invitation.all_actions_dropped",
                                        "Well done: your results already exceed what our proposed actions would achieve. You can still add your own actions.",
                                    )}
                                </Typography>
                            : projection.status === "error" ?
                                <EstimateErrorAlert reason={projection.reason} onRetry={onRetryEstimate} />
                            : projection.status === "conflict" ?
                                <Alert
                                    severity="warning"
                                    role="status"
                                    sx={{
                                        border: 1,
                                        borderColor: "warning.main",
                                        borderRadius: 1,
                                        backgroundColor: alpha(theme.palette.warning.main, 0.04),
                                    }}
                                >
                                    {t(
                                        "action_plan.invitation.plan_exists",
                                        "An action plan already exists for this diagnostic. Close this window to view it.",
                                    )}
                                </Alert>
                            :   scores.map((score) => {
                                    let value: number | null = null;
                                    if (projection.status === "success") {
                                        value = projection.values[score.id] ?? null;
                                    }
                                    const precision = getScorePrecision(score);
                                    const color =
                                        (value === null ? undefined : (
                                            getImpactColor(value, precision, score.improvement, theme)
                                        )) ?? theme.palette.text.secondary;
                                    return (
                                        <Stack
                                            key={`${score.id}-invitation-score`}
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            gap={theme.spacing(2)}
                                            sx={{
                                                p: theme.spacing(2),
                                                border: 1,
                                                borderColor: "divider",
                                                borderRadius: 1,
                                            }}
                                        >
                                            <Typography component="p" variant="body-regular">
                                                {score.title}
                                            </Typography>
                                            {projection.status === "loading" ?
                                                <Skeleton
                                                    width={theme.spacing(15)}
                                                    aria-label={t(
                                                        "action_plan.invitation.loading_estimate",
                                                        "Loading estimate…",
                                                    )}
                                                />
                                            :   <Typography
                                                    component="p"
                                                    variant="body-regular"
                                                    sx={{ color, flexShrink: 0 }}
                                                >
                                                    {value === null && (
                                                        <span
                                                            aria-label={t(
                                                                "action_plan.invitation.not_available",
                                                                "Not available",
                                                            )}
                                                        >
                                                            —
                                                        </span>
                                                    )}
                                                    {value !== null && (
                                                        <ApproximateImpactTooltip
                                                            value={value}
                                                            precision={precision}
                                                            unit={score.unit?.abbreviation ?? ""}
                                                        >
                                                            {`${formatImpact(value, precision, i18n.language, { signDisplay: "exceptZero" })} ${score.unit?.abbreviation ?? ""}`}
                                                        </ApproximateImpactTooltip>
                                                    )}
                                                </Typography>
                                            }
                                        </Stack>
                                    );
                                })
                            }
                        </>
                    :   <Typography component="p" variant="body-regular">
                            {t(
                                "action_plan.invitation.create_prompt",
                                "Build your action plan now to start improving your results.",
                            )}
                        </Typography>
                    }
                </Stack>
            </DialogContent>
            <DialogActions sx={{ p: theme.spacing(4), borderTop: 1, borderColor: "divider" }}>
                <Stack gap={theme.spacing(2)} width="100%">
                    {notice && <InvitationNoticeAlert notice={notice} />}
                    {creationUnconfirmed && !notice && (
                        <Alert
                            severity="warning"
                            icon={<WarningAmberOutlined />}
                            sx={{
                                border: 1,
                                borderColor: "warning.main",
                                borderRadius: 1,
                                backgroundColor: alpha(theme.palette.warning.main, 0.04),
                            }}
                        >
                            {t(
                                "action_plan.invitation.creation_still_processing",
                                "Your action plan may still be processing. Check again before trying to create it again.",
                            )}
                        </Alert>
                    )}
                    {failed && !notice && (
                        <Alert
                            severity="error"
                            icon={<WarningAmberOutlined />}
                            sx={{
                                border: 1,
                                borderColor: "error.main",
                                borderRadius: 1,
                                backgroundColor: alpha(theme.palette.error.main, 0.04),
                            }}
                        >
                            {t(
                                "action_plan.invitation.creation_failed_confirmed",
                                "We couldn't create your action plan. Nothing was saved; you can try again.",
                            )}
                        </Alert>
                    )}
                    {proposed && (
                        <Button
                            variant="primary"
                            fullWidth
                            loading={busy && creationType === "adopt"}
                            loadingPosition="start"
                            disabled={
                                projection.status === "conflict" ||
                                adoptDisabled ||
                                (busy && creationType !== "adopt") ||
                                (creationUnconfirmed && creationType !== "adopt")
                            }
                            onClick={creationUnconfirmed && creationType === "adopt" ? onCheckCreation : onAdopt}
                        >
                            {creationUnconfirmed && creationType === "adopt" ?
                                t("action_plan.invitation.check_creation", "Check again")
                            : busy && creationType === "adopt" ?
                                t("action_plan.invitation.creating_plan", "Creating your action plan…")
                            : retryAdoption ?
                                t("action_plan.invitation.retry", "Try again")
                            :   t("action_plan.invitation.adopt", "View proposed actions")}
                        </Button>
                    )}
                    <Button
                        variant={proposed ? "secondary" : "primary"}
                        fullWidth
                        loading={busy && creationType === "create-own"}
                        loadingPosition="start"
                        disabled={
                            projection.status === "conflict" ||
                            (busy && creationType !== "create-own") ||
                            (creationUnconfirmed && creationType !== "create-own")
                        }
                        onClick={creationUnconfirmed && creationType === "create-own" ? onCheckCreation : onCreateOwn}
                    >
                        {creationUnconfirmed && creationType === "create-own" ?
                            t("action_plan.invitation.check_creation", "Check again")
                        : busy && creationType === "create-own" ?
                            t("action_plan.invitation.creating_plan", "Creating your action plan…")
                        : failed && creationType === "create-own" ?
                            t("action_plan.invitation.retry", "Try again")
                        :   t("action_plan.invitation.create_own", "Create my own plan")}
                    </Button>
                    <Button variant="text" fullWidth disabled={busy} onClick={close}>
                        {t("action_plan.invitation.later", "Decide later")}
                    </Button>
                    {takingLonger && (
                        <Typography
                            component="p"
                            variant="body-regular"
                            role="status"
                            aria-live="polite"
                            sx={{ textAlign: "center" }}
                        >
                            {creationType === "adopt" ?
                                t(
                                    "action_plan.invitation.creation_in_progress",
                                    "Still working… Your action plan is being prepared. Please keep this window open.",
                                )
                            :   t(
                                    "action_plan.creation.taking_longer_own",
                                    "Still working… Creating your action plan can take up to a minute. Please keep this window open.",
                                )
                            }
                        </Typography>
                    )}
                </Stack>
            </DialogActions>
        </Dialog>
    );
};
